import React from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    Button
} from 'shared/components/ui/button';
import {
    Plus, X
} from 'lucide-react';
import {
    GovernanceBlock, EscalationRule
} from '../types/builderAssistant.types';
import ChipInput from './ChipInput';

interface GovernanceSectionProps {
  value: GovernanceBlock;
  onChange: (next: GovernanceBlock) => void;
}

export default function GovernanceSection({
 value, onChange
}: GovernanceSectionProps) {
    const update = (patch: Partial<GovernanceBlock>) => onChange({
 ...value,
...patch
});

    const updateRule = (i: number, patch: Partial<EscalationRule>) => {
        const next = [...value.escalation];
        next[i] = {
 ...next[i],
...patch
};
        update({
 escalation: next
});
    };

    const addRule = () => update({
 escalation: [...value.escalation, {
 condition: '',
action: ''
}]
});
    const removeRule = (i: number) => update({
 escalation: value.escalation.filter((_, idx) => idx !== i)
});

    return (
        <div className="space-y-4">
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                        informational today
                    </span>
                    <span className="font-medium">Stored on the assistant, not yet enforced at runtime.</span>
                </div>
                This section is planned for future actions. The assistant doc
                persists everything you set here so future governance wiring
                can read it without another migration; Mind currently does not
                check allowed_actions / denied_actions / escalation / boundaries
                on execute requests.
            </div>

            <div className="space-y-1">
                <Label>Allowed actions</Label>
                <ChipInput
                    values={value.allowed_actions}
                    onChange={(v) => update({
 allowed_actions: v
})}
                    placeholder="e.g. lookup_order"
                />
                <p className="text-xs text-muted-foreground">
                    Empty = no explicit allow-list (deny-list still enforced).
                </p>
            </div>

            <div className="space-y-1">
                <Label>Denied actions</Label>
                <ChipInput
                    values={value.denied_actions}
                    onChange={(v) => update({
 denied_actions: v
})}
                    placeholder="e.g. issue_refund"
                />
            </div>

            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <Label>Escalation rules</Label>
                    <Button variant="outline" size="sm" onClick={addRule}>
                        <Plus className="mr-1 h-3 w-3" /> Add rule
                    </Button>
                </div>
                {value.escalation.length === 0 && (
                    <div className="text-xs text-muted-foreground italic">No rules.</div>
                )}
                <div className="space-y-2">
                    {value.escalation.map((rule, i) => (
                        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                            <Input
                                value={rule.condition}
                                onChange={(e) => updateRule(i, {
 condition: e.target.value
})}
                                placeholder="condition (e.g. refund_amount > 100)"
                                className="text-xs font-mono"
                            />
                            <Input
                                value={rule.action}
                                onChange={(e) => updateRule(i, {
 action: e.target.value
})}
                                placeholder="action (e.g. transfer_to_human)"
                                className="text-xs"
                            />
                            <Button variant="ghost" size="sm" onClick={() => removeRule(i)}>
                                <X className="h-3 w-3" />
                            </Button>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-1">
                <Label>Boundaries</Label>
                <Textarea
                    value={value.boundaries}
                    onChange={(e) => update({
 boundaries: e.target.value
})}
                    placeholder="Free-text boundaries the assistant should respect (max 4000 chars)"
                    rows={4}
                />
            </div>
        </div>
    );
}
